'use client';

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

interface Equipment {
  id: number;
  name: string;
  image: string;
  alt: string;
}

const medicalEquipment: Equipment[] = [
  {
    id: 1,
    name: "Genoray OSCAR 15 – C-Arm X-ray System",
    image: "/assets/images/c_arm_genoray_oscar_fifteen_hero.png",
    alt: "Genoray OSCAR 15 C-Arm X-ray System for surgical imaging"
  },
  {
    id: 2,
    name: "C-Arm Compatible Operating Table (Radiolucent)",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1572c3442-1764773194635.png",
    alt: "Radiolucent operating table compatible with C-Arm imaging systems"
  },
  {
    id: 3,
    name: "PACS System (Imaging & Archiving)",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e5dcb682-1764709041489.png",
    alt: "PACS medical imaging and archiving system with digital displays"
  },
  {
    id: 4,
    name: "C-Arm Positioning Accessories",
    image: "/assets/images/c_arm_positioning_accessories.png",
    alt: "C-Arm positioning accessories and mounting equipment"
  },
  {
    id: 5,
    name: "Dual Monitor Cart / C-Arm Monitor Stand",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cb0883e9-1765170816017.png",
    alt: "Mobile dual monitor cart for C-Arm imaging display"
  },
  {
    id: 6,
    name: "Foot Switch Controls",
    image: "/assets/images/medical_foot_switch_controls.png",
    alt: "Medical foot switch controls for hands-free operation"
  },
  {
    id: 7,
    name: "C-Arm Sterile Drapes & Covers",
    image: "/assets/images/c_arm_sterile_drapes_covers.png",
    alt: "Sterile drapes and protective covers for C-Arm equipment"
  },
  {
    id: 8,
    name: "Medical Dry Laser Printer (X-ray Films)",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9f023ce-1765618337952.png",
    alt: "Medical dry laser printer for X-ray film printing"
  }
];

export default function FeaturedEquipment() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const slideInterval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % medicalEquipment.length;
        
        // Smooth scroll to next item
        const itemWidth = scrollContainer.children[0]?.clientWidth || 0;
        const gap = 32; // 8 * 4 = 32px gap
        const scrollPosition = nextIndex * (itemWidth + gap);
        
        scrollContainer.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
        
        return nextIndex;
      });
    }, 2000); // Slide every 2 seconds

    return () => {
      clearInterval(slideInterval);
    };
  }, []);

  return (
    <section className="py-16 lg:py-24 bg-surface overflow-hidden">
      <div className="max-w-full px-4 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
            <span className="text-sm font-semibold text-primary">Featured Equipment</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-4">
            Premium Medical Equipment Solutions
          </h2>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-8 overflow-x-hidden scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {medicalEquipment.map((equipment) => (
            <div
              key={equipment.id}
              className="flex-shrink-0 w-80 bg-card rounded-lg overflow-hidden shadow-brand hover:shadow-brand-lg transition-all duration-300 group"
            >
              <div className="relative h-80 overflow-hidden">
                <AppImage
                  src={equipment.image}
                  alt={equipment.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              
              <div className="p-6 bg-gradient-to-b from-card to-background">
                <h3 className="text-lg font-bold text-foreground text-center leading-tight">
                  {equipment.name}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}